import { isNotNull } from '../typeGuards';
import {
  getErrors,
  getResponses,
  isResponseOrError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../../types/responses';
import { reduceSync, SyncReduceFunction } from './sync';

export type AsyncMapFunction<T, U> = (
  item: T,
  index: number,
  array: T[]
) => Promise<ResponseOrError<U>> | Promise<U>;

export const mapAsync = async <T, U>(
  arr: T[],
  mapFn: AsyncMapFunction<T, U>
): Promise<ResponseOrError<U[]>> => {
  try {
    const items = await Promise.all(arr.map(mapFn));

    const responseOrErrorItems = items.map(item => {
      if (isResponseOrError(item)) {
        return item;
      }

      return wrapSuccess(item);
    });

    const errors = getErrors(responseOrErrorItems);

    if (errors.length) {
      return wrapError(new Error(errors.map(e => e.message).join(', ')));
    }

    const responses = getResponses(responseOrErrorItems);

    return wrapSuccess(responses);
  } catch (err) {
    return wrapError(err as Error);
  }
};

export const filterAsync = async <T, U>(
  arr: T[],
  mapFn: AsyncMapFunction<T, U | null>
): Promise<ResponseOrError<U[]>> => {
  const [failedToMap, itemsOrNulls] = await mapAsync(arr, mapFn);

  if (failedToMap) {
    return wrapError(failedToMap);
  }

  const filteredItems = itemsOrNulls.filter(isNotNull);

  return wrapSuccess(filteredItems);
};

export const everyAsync = async <T>(
  arr: T[],
  mapFn: AsyncMapFunction<T, boolean>
) => {
  const [failedToMap, results] = await mapAsync(arr, mapFn);

  if (failedToMap) {
    return wrapError(failedToMap);
  }

  return wrapSuccess(results.every(Boolean));
};

export const reduceAsync = async <T, U, V>(
  arr: T[],
  mapFn: AsyncMapFunction<T, U>,
  reduceFn: SyncReduceFunction<U, V>,
  initialValue: V
) => {
  const [failedToMap, results] = await mapAsync(arr, mapFn);

  if (failedToMap) {
    return wrapError(failedToMap);
  }

  const [error, result] = reduceSync(results, reduceFn, initialValue);

  if (error) {
    return wrapError(error);
  }

  return wrapSuccess(result);
};
